import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MapPin, ChevronDown, Clock, Heart, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_crazy-shaw-10/artifacts/8444q5ej_WhatsApp%20Image%202026-07-03%20at%2020.53.38.jpeg";
const WEDDING_DATE = new Date("2026-10-17T15:00:00");
const MAPS_URL = "https://maps.app.goo.gl/6XDhdDiB5DvydV899?g_st=iw";
const MAPS_EMBED = "https://www.google.com/maps?q=Qasr%20Al%20Qamar%20Route%20de%20Tanger%20K%C3%A9nitra%20Morocco&output=embed";

// ---------------- i18n ----------------
const T = {
  it: {
    seal_tap: "Tocca per aprire l'invito",
    hero_sub: "Siete invitati al matrimonio di",
    hero_date: "17 Ottobre 2026",
    hero_loc: "Kénitra, Marocco",
    hero_rsvp: "Conferma la presenza",
    scroll: "Scorri",
    count_title: "Il Conto alla Rovescia",
    count_sub: "Il grande giorno si avvicina",
    days: "Giorni", hours: "Ore", minutes: "Minuti", seconds: "Secondi",
    wedding_day: "È il giorno del matrimonio!",
    loc_kicker: "Dove e Quando", loc_title: "La Location",
    loc_sub: "Dove celebreremo il nostro amore",
    loc_time: "Ore 15:00", loc_city: "Qasr Al Qamar",
    loc_addr: ["a coté Setexam, Hay Assam,", "Route de Tanger, Kénitra 14000,", "Marocco"],
    directions: "Ottieni Indicazioni",
    rsvp_title: "Conferma Presenza", rsvp_sub: "Si prega di rispondere entro il 10 settembre",
    name: "Nome Completo", name_ph: "Il tuo nome e cognome",
    attending: "Verrai?", yes: "Sì, ci sarò!", no: "Purtroppo non potrò esserci",
    guests: "Numero di Ospiti (incluso te)", guests_names: "Nome e cognome degli ospiti", guests_names_ph: "Scrivi il tuo nome e quello degli altri ospiti (uno per riga o separati da virgola)", guest_label: "Ospite", children: "Ci saranno bambini?",
    children_ph: "Es. Sì, 2 bambini (3 e 6 anni) / No", children_yes: "Sì", children_no: "No", children_count: "Quanti bambini?",
    submit: "Invia Conferma", sending: "Invio in corso...",
    ok: "Grazie! La tua risposta è stata salvata.", err: "Si è verificato un errore. Riprova più tardi.",
    contacts: "Contatti", bride: "Dounya", groom: "Yassine",
  },
  en: {
    seal_tap: "Tap the seal to open",
    hero_sub: "You are invited to the wedding of",
    hero_date: "October 17, 2026",
    hero_loc: "Kénitra, Morocco",
    hero_rsvp: "RSVP",
    scroll: "Scroll",
    count_title: "Countdown",
    count_sub: "The big day is approaching",
    days: "Days", hours: "Hours", minutes: "Minutes", seconds: "Seconds",
    wedding_day: "It's the wedding day!",
    loc_kicker: "Where & When", loc_title: "The Venue",
    loc_sub: "Where we will celebrate our love",
    loc_time: "3:00 PM", loc_city: "Qasr Al Qamar",
    loc_addr: ["next to Setexam, Hay Assam,", "Route de Tanger, Kénitra 14000,", "Morocco"],
    directions: "Get Directions",
    rsvp_title: "RSVP", rsvp_sub: "Please reply by September 10th",
    name: "Full Name", name_ph: "Your first and last name",
    attending: "Will you attend?", yes: "Yes, I'll be there!", no: "Sorry, I can't make it",
    guests: "Number of Guests (including you)", guests_names: "Full names of guests", guests_names_ph: "Write your name and the other guests' names (one per line or comma-separated)", guest_label: "Guest", children: "Will there be children?",
    children_ph: "e.g. Yes, 2 children (3 and 6 y.o.) / No", children_yes: "Yes", children_no: "No", children_count: "How many children?",
    submit: "Send Confirmation", sending: "Sending...",
    ok: "Thank you! Your reply has been saved.", err: "An error occurred. Please try again later.",
    contacts: "Contacts", bride: "Dounya", groom: "Yassine",
  },
  fr: {
    seal_tap: "Touchez pour ouvrir l'invitation",
    hero_sub: "Vous êtes invités au mariage de",
    hero_date: "17 Octobre 2026",
    hero_loc: "Kénitra, Maroc",
    hero_rsvp: "Confirmer",
    scroll: "Défiler",
    count_title: "Le Compte à Rebours",
    count_sub: "Le grand jour approche",
    days: "Jours", hours: "Heures", minutes: "Minutes", seconds: "Secondes",
    wedding_day: "C'est le jour du mariage !",
    loc_kicker: "Où et Quand", loc_title: "Le Lieu",
    loc_sub: "Où nous célébrerons notre amour",
    loc_time: "15h00", loc_city: "Qasr Al Qamar",
    loc_addr: ["à côté de Setexam, Hay Assam,", "Route de Tanger, Kénitra 14000,", "Maroc"],
    directions: "Obtenir l'itinéraire",
    rsvp_title: "Confirmation", rsvp_sub: "Merci de répondre avant le 10 septembre",
    name: "Nom Complet", name_ph: "Votre nom et prénom",
    attending: "Viendrez-vous ?", yes: "Oui, je serai là !", no: "Désolé, je ne peux pas venir",
    guests: "Nombre d'invités (vous inclus)", guests_names: "Nom et prénom des invités", guests_names_ph: "Indiquez votre nom et celui des autres invités (un par ligne ou séparés par une virgule)", guest_label: "Invité", children: "Y aura-t-il des enfants ?",
    children_ph: "Ex. Oui, 2 enfants (3 et 6 ans) / Non", children_yes: "Oui", children_no: "Non", children_count: "Combien d'enfants ?",
    submit: "Envoyer la confirmation", sending: "Envoi...",
    ok: "Merci ! Votre réponse a été enregistrée.", err: "Une erreur est survenue. Réessayez plus tard.",
    contacts: "Contacts", bride: "Dounya", groom: "Yassine",
  },
  ar: {
    seal_tap: "اضغط لفتح الدعوة",
    hero_sub: "أنتم مدعوون لحضور زفاف",
    hero_date: "17 أكتوبر 2026",
    hero_loc: "القنيطرة، المغرب",
    hero_rsvp: "تأكيد الحضور",
    scroll: "مرّر",
    count_title: "العد التنازلي",
    count_sub: "اليوم الكبير يقترب",
    days: "أيام", hours: "ساعات", minutes: "دقائق", seconds: "ثوانٍ",
    wedding_day: "إنه يوم الزفاف!",
    loc_kicker: "أين ومتى", loc_title: "المكان",
    loc_sub: "حيث سنحتفل بحبنا",
    loc_time: "الساعة 15:00", loc_city: "Qasr Al Qamar",
    loc_addr: ["بجانب Setexam، حي عسام،", "طريق طنجة، القنيطرة 14000،", "المغرب"],
    directions: "احصل على الاتجاهات",
    rsvp_title: "تأكيد الحضور", rsvp_sub: "يرجى الرد قبل 10 سبتمبر",
    name: "الاسم الكامل", name_ph: "اسمك ولقبك",
    attending: "هل ستحضر؟", yes: "نعم، سأحضر!", no: "للأسف لن أتمكن من الحضور",
    guests: "عدد الضيوف (بما فيهم أنت)", guests_names: "الأسماء الكاملة للضيوف", guests_names_ph: "اكتب اسمك وأسماء بقية الضيوف (كل اسم في سطر أو مفصولة بفاصلة)", guest_label: "الضيف", children: "هل سيحضر أطفال؟",
    children_ph: "مثال: نعم، طفلان / لا", children_yes: "نعم", children_no: "لا", children_count: "كم عدد الأطفال؟",
    submit: "إرسال التأكيد", sending: "جارٍ الإرسال...",
    ok: "شكراً! تم حفظ ردك.", err: "حدث خطأ. حاول مرة أخرى لاحقاً.",
    contacts: "التواصل", bride: "Dounya", groom: "Yassine",
  },
};

const Floral = ({ className }) => (
  <svg className={className} viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M10 140 Q30 100 70 80 Q50 60 20 50 Q40 40 60 55 Q80 30 75 10 Q90 30 85 55 Q110 40 130 45 Q100 60 85 75 Q120 95 140 130" stroke="#C08081" strokeWidth="1.5" fill="none" />
    <path d="M40 120 Q50 90 75 75 Q60 65 45 70" stroke="#C08081" strokeWidth="1" fill="none" opacity="0.6" />
    <circle cx="75" cy="75" r="4" fill="#C08081" opacity="0.4" />
    <circle cx="55" cy="55" r="2.5" fill="#C08081" opacity="0.3" />
    <circle cx="95" cy="60" r="2.5" fill="#C08081" opacity="0.3" />
  </svg>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className}
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.8, delay, ease: "easeOut" }}>
    {children}
  </motion.div>
);

// ---------------- Opening Splash (photo + names) ----------------
const Envelope = ({ onOpen, t }) => {
  const [opening, setOpening] = useState(false);
  const handle = () => {
    if (opening) return;
    setOpening(true);
    setTimeout(onOpen, 850);
  };
  return (
    <motion.div className="fixed inset-0 z-50 flex flex-col items-center justify-center cursor-pointer overflow-hidden"
      exit={{ opacity: 0 }} transition={{ duration: 0.6 }} onClick={handle} data-testid="wax-seal-envelope">
      {/* photo background */}
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Dounya & Yassine" className="w-full h-full object-cover object-[center_22%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-dark-charcoal/30 via-dark-charcoal/15 to-dark-charcoal/55" />
      </div>

      <motion.div className="relative z-10 flex flex-col items-center px-8 text-center text-cream-bg"
        style={{ textShadow: "0 2px 14px rgba(0,0,0,0.6)" }}
        initial={{ opacity: 0, y: 20 }}
        animate={opening ? { scale: 1.06, opacity: 0 } : { scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeInOut" }} data-testid="wax-seal-button">
        <span className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-cream-bg/90 font-semibold mb-5">{t.hero_sub}</span>
        <h1 className="font-script text-6xl md:text-8xl leading-none drop-shadow-lg">Dounya</h1>
        <span className="font-script text-3xl md:text-5xl text-rose-blush my-2 drop-shadow">&amp;</span>
        <h1 className="font-script text-6xl md:text-8xl leading-none drop-shadow-lg">Yassine</h1>
        <div className="flex items-center gap-3 mt-7">
          <span className="h-px w-8 bg-cream-bg/70" />
          <span className="text-xs md:text-sm tracking-[0.3em] text-cream-bg/90">17 · 10 · 2026</span>
          <span className="h-px w-8 bg-cream-bg/70" />
        </div>
      </motion.div>
      <motion.p className="absolute bottom-12 z-10 text-xs md:text-sm uppercase tracking-[0.3em] text-cream-bg/90 font-semibold"
        animate={{ opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 2 }}>
        {t.seal_tap}
      </motion.p>
    </motion.div>
  );
};

// ---------------- Countdown ----------------
const calcCountdown = () => {
  const d = WEDDING_DATE.getTime() - Date.now();
  if (d < 0) return null;
  return { days: Math.floor(d / 86400000), hours: Math.floor((d % 86400000) / 3600000), minutes: Math.floor((d % 3600000) / 60000), seconds: Math.floor((d % 60000) / 1000) };
};

const Countdown = ({ t }) => {
  const [c, setC] = useState(calcCountdown());
  useEffect(() => { const id = setInterval(() => setC(calcCountdown()), 1000); return () => clearInterval(id); }, []);
  if (!c) return <p className="font-serif text-2xl text-dark-charcoal">{t.wedding_day}</p>;
  const items = [{ v: c.days, l: t.days }, { v: c.hours, l: t.hours }, { v: c.minutes, l: t.minutes }, { v: c.seconds, l: t.seconds }];
  return (
    <div className="flex items-center justify-center gap-2 md:gap-5" data-testid="countdown-timer">
      {items.map((it, i) => (
        <div key={it.l} className="flex items-center gap-2 md:gap-5">
          <div className="flex flex-col items-center">
            <span className="font-serif text-4xl md:text-6xl font-medium text-dark-charcoal tabular-nums" data-testid={`countdown-${i}`}>{String(it.v).padStart(2, "0")}</span>
            <span className="text-[10px] md:text-xs uppercase tracking-widest text-rose-antique mt-2">{it.l}</span>
          </div>
          {i < 3 && <span className="font-serif text-3xl md:text-5xl text-rose-antique/50 -mt-4">:</span>}
        </div>
      ))}
    </div>
  );
};

// ---------------- RSVP ----------------
const RsvpForm = ({ t }) => {
  const [form, setForm] = useState({ attending: "true", guests_count: 1, names: [""], children: "no", children_count: 1 });
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  const setCount = (n) => {
    const count = Math.min(10, Math.max(1, n));
    setForm((f) => {
      const names = [...f.names];
      if (count > names.length) {
        while (names.length < count) names.push("");
      } else {
        names.length = count;
      }
      return { ...f, guests_count: count, names };
    });
  };

  const setName = (i, val) => {
    setForm((f) => {
      const names = [...f.names];
      names[i] = val;
      return { ...f, names };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setStatus({ type: "", msg: "" });
    const attending = form.attending === "true";
    const cleanNames = form.names.map((n) => n.trim()).filter(Boolean);
    const fullName = cleanNames[0] || "";
    const childrenInfo = attending ? (form.children === "yes" ? String(form.children_count) : "0") : "";
    try {
      await axios.post(`${API}/rsvp`, {
        full_name: fullName,
        attending,
        guests_count: attending ? form.guests_count : 0,
        guests_names: attending ? cleanNames.join("\n") : "",
        children_info: childrenInfo,
      });
      setStatus({ type: "ok", msg: t.ok });
      setForm({ attending: "true", guests_count: 1, names: [""], children: "no", children_count: 1 });
    } catch { setStatus({ type: "err", msg: t.err }); } finally { setLoading(false); }
  };

  const input = "w-full bg-white border border-rose-antique/40 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-antique transition-colors";
  const label = "block text-xs uppercase tracking-wider text-dark-charcoal mb-2 font-medium";
  const attending = form.attending === "true";
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="rsvp-form">
      {/* Step 1: Attending */}
      <div>
        <label className={label}>{t.attending} *</label>
        <select value={form.attending} onChange={(e) => setForm({ ...form, attending: e.target.value })} className={input} data-testid="rsvp-attending-select">
          <option value="true">{t.yes}</option>
          <option value="false">{t.no}</option>
        </select>
      </div>

      {attending ? (
        <>
          {/* Step 2: Number of guests */}
          <div>
            <label className={label}>{t.guests}</label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setCount(form.guests_count - 1)} className="w-11 h-11 rounded-xl border border-rose-antique/40 bg-white text-lg hover:bg-rose-blush/30 transition-colors" data-testid="rsvp-guests-decrement">−</button>
              <span className="font-serif text-2xl w-10 text-center" data-testid="rsvp-guests-count">{form.guests_count}</span>
              <button type="button" onClick={() => setCount(form.guests_count + 1)} className="w-11 h-11 rounded-xl border border-rose-antique/40 bg-white text-lg hover:bg-rose-blush/30 transition-colors" data-testid="rsvp-guests-increment">+</button>
            </div>
          </div>

          {/* Step 3: Dynamic name boxes */}
          <div className="space-y-3">
            <label className={label}>{t.guests_names}</label>
            {form.names.map((n, i) => (
              <input
                key={i}
                type="text"
                required
                value={n}
                onChange={(e) => setName(i, e.target.value)}
                className={input}
                placeholder={i === 0 ? t.name_ph : `${t.guest_label} ${i + 1}`}
                data-testid={`rsvp-guest-name-${i}`}
              />
            ))}
          </div>

          {/* Children */}
          <div>
            <label className={label}>{t.children}</label>
            <select value={form.children} onChange={(e) => setForm({ ...form, children: e.target.value })} className={input} data-testid="rsvp-children-select">
              <option value="no">{t.children_no}</option>
              <option value="yes">{t.children_yes}</option>
            </select>
            {form.children === "yes" && (
              <div className="mt-3">
                <label className={label}>{t.children_count}</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setForm({ ...form, children_count: Math.max(1, form.children_count - 1) })} className="w-11 h-11 rounded-xl border border-rose-antique/40 bg-white text-lg hover:bg-rose-blush/30 transition-colors" data-testid="rsvp-children-decrement">−</button>
                  <span className="font-serif text-2xl w-10 text-center" data-testid="rsvp-children-count">{form.children_count}</span>
                  <button type="button" onClick={() => setForm({ ...form, children_count: Math.min(10, form.children_count + 1) })} className="w-11 h-11 rounded-xl border border-rose-antique/40 bg-white text-lg hover:bg-rose-blush/30 transition-colors" data-testid="rsvp-children-increment">+</button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Not attending: just the name to know who declined */
        <div>
          <label className={label}>{t.name} *</label>
          <input type="text" required value={form.names[0]} onChange={(e) => setName(0, e.target.value)} className={input} placeholder={t.name_ph} data-testid="rsvp-name-input" />
        </div>
      )}

      <button type="submit" disabled={loading} className="w-full bg-dark-charcoal hover:bg-rose-antique text-cream-bg py-4 rounded-xl font-medium text-xs tracking-widest uppercase transition-all duration-300 shadow-lg disabled:opacity-60" data-testid="rsvp-submit-btn">
        {loading ? t.sending : t.submit}
      </button>
      {status.msg && <p className={`text-center text-sm font-medium ${status.type === "ok" ? "text-green-700" : "text-red-600"}`} data-testid="rsvp-status">{status.msg}</p>}
    </form>
  );
};

// ---------------- Gift / IBAN section ----------------
const IBAN = "IT02K0306901629100000078611";
const GIFT = {
  it: {
    kicker: "Un pensiero", title: "Lista Nozze",
    lines: ["Cari amici e parenti,", "la vostra presenza è tutto ciò che desideriamo per rendere speciale questa giornata.", "Se però vorrete farci un regalo, saremo felici di ricevere sia un pensiero da scartare sia un contributo per il viaggio dei nostri sogni."],
    holder: "Intestato a Dounya Ait Sgheir e Yassine Benichou", copy: "Copia IBAN", copied: "Copiato!", thanks: "Grazie di cuore,", signature: "Dounya e Yassine",
  },
  en: {
    kicker: "A little something", title: "Wedding Gift",
    lines: ["Dear friends and family,", "your presence is all we wish for to make this day special.", "If you would like to give us a gift, we would be happy with either a present to unwrap or a contribution towards our dream honeymoon."],
    holder: "In the name of Dounya Ait Sgheir and Yassine Benichou", copy: "Copy IBAN", copied: "Copied!", thanks: "Heartfelt thanks,", signature: "Dounya & Yassine",
  },
  fr: {
    kicker: "Une petite attention", title: "Liste de Mariage",
    lines: ["Chers amis et famille,", "votre présence est tout ce que nous souhaitons pour rendre cette journée spéciale.", "Si toutefois vous souhaitez nous offrir un cadeau, nous serons heureux d'un présent à déballer comme d'une contribution pour notre voyage de rêve."],
    holder: "Au nom de Dounya Ait Sgheir et Yassine Benichou", copy: "Copier l'IBAN", copied: "Copié !", thanks: "Merci du fond du cœur,", signature: "Dounya et Yassine",
  },
  ar: {
    kicker: "هدية", title: "قائمة الزفاف",
    lines: ["أصدقاءنا وأحباءنا الأعزاء،", "حضوركم هو كل ما نتمناه لجعل هذا اليوم مميزاً.", "وإن أردتم إهداءنا شيئاً، فسنكون سعداء بهدية أو بمساهمة في رحلة أحلامنا."],
    holder: "باسم Dounya Ait Sgheir و Yassine Benichou", copy: "نسخ IBAN", copied: "تم النسخ!", thanks: "شكراً من القلب،", signature: "Dounya و Yassine",
  },
};

const GiftSection = ({ lang }) => {
  const g = GIFT[lang];
  const [copied, setCopied] = useState(false);
  const copyIban = async () => {
    try { await navigator.clipboard.writeText(IBAN); }
    catch {
      const el = document.createElement("textarea");
      el.value = IBAN; document.body.appendChild(el); el.select();
      document.execCommand("copy"); document.body.removeChild(el);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <section className="py-24 px-6 bg-rose-blush/20 border-t border-rose-antique/20" data-testid="gift-section">
      <Reveal className="max-w-xl mx-auto bg-cream-bg p-8 md:p-12 rounded-3xl border border-rose-antique/30 shadow-xl text-center space-y-7">
        <div className="flex items-center justify-center gap-3 text-rose-antique">
          <span className="h-px w-10 bg-rose-antique/50" /><Heart className="w-4 h-4" /><span className="h-px w-10 bg-rose-antique/50" />
        </div>
        <div className="space-y-2">
          <span className="text-xs uppercase tracking-[0.25em] text-rose-antique font-semibold">{g.kicker}</span>
        </div>
        <div className="space-y-4 text-sm md:text-base text-dark-charcoal/80 leading-relaxed">
          {g.lines.map((l) => <p key={l}>{l}</p>)}
        </div>
        <div className="bg-rose-blush/20 border border-rose-antique/30 rounded-2xl p-6 space-y-3">
          <p className="text-[11px] uppercase tracking-[0.2em] text-rose-antique font-semibold">IBAN</p>
          <p className="font-mono text-[12px] sm:text-base md:text-lg tracking-tight sm:tracking-wider text-dark-charcoal whitespace-nowrap overflow-x-auto select-all" data-testid="gift-iban" dir="ltr">{IBAN}</p>
          <p className="text-xs text-dark-charcoal/60">{g.holder}</p>
          <button onClick={copyIban} className="inline-flex items-center justify-center gap-2 mt-2 bg-dark-charcoal hover:bg-rose-antique text-cream-bg py-2.5 px-5 rounded-full text-xs tracking-widest uppercase transition-colors shadow" data-testid="gift-copy-btn">
            {copied ? <><Check className="w-4 h-4" /> {g.copied}</> : <><Copy className="w-4 h-4" /> {g.copy}</>}
          </button>
        </div>
        <div className="pt-1 space-y-1">
          <p className="text-sm text-dark-charcoal/80">{g.thanks}</p>
          <p className="font-script text-3xl md:text-4xl text-rose-antique">{g.signature}</p>
        </div>
      </Reveal>
    </section>
  );
};

// ---------------- Main ----------------
export default function WeddingPage({ showGift = false }) {
  const [opened, setOpened] = useState(false);
  const [lang, setLang] = useState("it");
  const t = T[lang];

  const open = () => {
    setOpened(true);
    document.body.style.overflow = "auto";
  };

  return (
    <div className="bg-wedding-pattern text-dark-charcoal font-sans antialiased" dir={lang === "ar" ? "rtl" : "ltr"}>
      <AnimatePresence>{!opened && <Envelope onOpen={open} t={t} />}</AnimatePresence>

      {/* Language switcher (always visible, also on the opening splash) */}
      <div className="fixed top-4 right-4 z-[60] flex gap-1 bg-cream-bg/85 backdrop-blur border border-rose-antique/30 rounded-full p-1 shadow" data-testid="lang-switcher" dir="ltr">
        {["it", "en", "fr", "ar"].map((l) => (
          <button key={l} onClick={(e) => { e.stopPropagation(); setLang(l); }} className={`min-w-9 h-8 px-2 rounded-full text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-dark-charcoal text-cream-bg" : "text-dark-charcoal/70 hover:bg-rose-blush/40"}`} data-testid={`lang-${l}`}>{l === "ar" ? "ع" : l}</button>
        ))}
      </div>

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center overflow-hidden" data-testid="home-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Dounya & Yassine" className="w-full h-full object-cover object-[center_22%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-charcoal/25 via-dark-charcoal/10 to-dark-charcoal/50" />
        </div>
        <motion.div className="relative z-10 px-6 space-y-5 text-cream-bg" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.6)" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-cream-bg/90">{t.hero_sub}</p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-tight">Dounya <span className="font-script text-rose-blush">&amp;</span> Yassine</h1>
          <div className="flex items-center justify-center gap-4">
            <span className="h-px w-10 bg-cream-bg/60" />
            <p className="font-serif text-lg md:text-2xl tracking-widest uppercase">{t.hero_date}</p>
            <span className="h-px w-10 bg-cream-bg/60" />
          </div>
          <p className="text-sm md:text-base tracking-widest uppercase text-cream-bg/90">{t.hero_loc}</p>
          <a href="#rsvp" className="inline-block mt-4 border border-cream-bg text-cream-bg hover:bg-cream-bg hover:text-dark-charcoal py-3 px-8 rounded-full text-xs tracking-widest uppercase transition-all duration-300" data-testid="hero-rsvp-btn">{t.hero_rsvp}</a>
        </motion.div>
        <a href="#countdown" className="absolute bottom-8 z-10 flex flex-col items-center gap-2 text-cream-bg/90 animate-bounce-slow" data-testid="scroll-arrow">
          <span className="text-[10px] tracking-widest uppercase">{t.scroll}</span>
          <ChevronDown className="w-6 h-6" />
        </a>
      </section>

      {/* COUNTDOWN */}
      <section id="countdown" className="py-24 px-6 bg-rose-blush/20 border-y border-rose-antique/20 scroll-mt-4" data-testid="countdown-section">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl text-dark-charcoal">{t.count_title}</h2>
            <p className="text-xs text-dark-charcoal/60 tracking-wider uppercase mt-3">{t.count_sub}</p>
          </Reveal>
          <Reveal delay={0.15}><Countdown t={t} /></Reveal>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-24 px-6 max-w-4xl mx-auto" data-testid="location-section">
        <Reveal className="text-center space-y-4 mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-rose-antique font-semibold">{t.loc_kicker}</span>
          <h2 className="font-serif text-3xl md:text-5xl text-dark-charcoal">{t.loc_title}</h2>
          <p className="text-xs text-dark-charcoal/60 tracking-wider uppercase">{t.loc_sub}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-cream-bg border border-rose-antique/30 rounded-3xl overflow-hidden shadow-xl md:flex">
            <div className="md:w-1/2 h-64 md:h-auto min-h-[300px]">
              <iframe title="map" src={MAPS_EMBED} className="w-full h-full min-h-[300px] border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
            <div className="p-8 md:p-12 md:w-1/2 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 bg-rose-blush/40 px-3 py-1 rounded-full text-xs font-semibold text-dark-charcoal tracking-wider uppercase">
                  <Clock className="w-3.5 h-3.5" /> {t.loc_time}
                </div>
                <h3 className="font-serif text-2xl text-dark-charcoal font-medium">{t.loc_city}</h3>
                <p className="text-sm text-dark-charcoal/70 leading-relaxed">{t.loc_addr.map((l) => <span key={l}>{l}<br /></span>)}</p>
              </div>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-dark-charcoal hover:bg-rose-antique text-cream-bg py-3.5 px-6 rounded-full transition-all duration-300 text-xs tracking-widest uppercase shadow-md" data-testid="directions-btn">
                <MapPin className="w-4 h-4" /> {t.directions}
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* RSVP */}
      <section id="rsvp" className="py-24 px-6 bg-rose-blush/10 scroll-mt-4" data-testid="rsvp-section">
        <Reveal className="max-w-xl mx-auto bg-cream-bg p-8 md:p-12 rounded-3xl border border-rose-antique/30 shadow-xl space-y-8">
          <div className="text-center space-y-3">
            <h2 className="font-serif text-3xl md:text-4xl text-dark-charcoal">{t.rsvp_title}</h2>
            <p className="text-xs text-dark-charcoal/60 tracking-wider uppercase">{t.rsvp_sub}</p>
          </div>
          <RsvpForm t={t} />
        </Reveal>
      </section>

      {showGift && <GiftSection lang={lang} />}

      {/* FOOTER */}
      <footer className="py-16 px-6 bg-cream-bg border-t border-rose-antique/20 text-center space-y-10" data-testid="footer-section">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center justify-center gap-3 text-rose-antique">
            <span className="h-px w-10 bg-rose-antique/50" /><span className="font-script text-3xl">D &amp; Y</span><span className="h-px w-10 bg-rose-antique/50" />
          </div>
          <h3 className="font-serif text-2xl text-dark-charcoal">{t.contacts}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
            <div className="space-y-1 p-4 bg-rose-blush/20 rounded-2xl border border-rose-antique/20">
              <p className="font-serif font-semibold text-lg text-dark-charcoal">{t.bride}</p>
              <p className="text-dark-charcoal/70">Tel: <a href="tel:+393463082531" className="hover:underline">+39 346 308 2531</a></p>
              <p className="text-dark-charcoal/70">Email: <a href="mailto:dounyasgheir@gmail.com" className="hover:underline">dounyasgheir@gmail.com</a></p>
            </div>
            <div className="space-y-1 p-4 bg-rose-blush/20 rounded-2xl border border-rose-antique/20">
              <p className="font-serif font-semibold text-lg text-dark-charcoal">{t.groom}</p>
              <p className="text-dark-charcoal/70">Tel: <a href="tel:+393495378543" className="hover:underline">+39 349 537 8543</a></p>
              <p className="text-dark-charcoal/70">Email: <a href="mailto:benichou.yassine@gmail.com" className="hover:underline">benichou.yassine@gmail.com</a></p>
            </div>
          </div>
        </div>
        <div className="text-xs text-dark-charcoal/40 pt-8 border-t border-rose-antique/10">
          <p>Dounya &amp; Yassine — 17 · 10 · 2026</p>
        </div>
      </footer>
    </div>
  );
}
